// Tipos do serviço `recovery` — Task 2: agregado LegalCase.
// Espelha o contrato do BFF (recovery/src/adapters/lambda/rest_api/app.py).

export const LegalCaseType = {
    EXTRAJUDICIAL_WORKOUT: 'EXTRAJUDICIAL_WORKOUT',
    JUDICIAL_EXECUTION: 'JUDICIAL_EXECUTION',
    EXECUTION_DEFENSE: 'EXECUTION_DEFENSE',
    MONITORY: 'MONITORY',
    JUDICIAL_REORGANIZATION: 'JUDICIAL_REORGANIZATION',
    LITIGATION_DEFENSE: 'LITIGATION_DEFENSE',
} as const satisfies Record<string, string>;

export type LegalCaseType = typeof LegalCaseType[keyof typeof LegalCaseType];

export const Pole = {
    ACTIVE: 'ACTIVE',
    PASSIVE: 'PASSIVE',
} as const satisfies Record<string, string>;

export type Pole = typeof Pole[keyof typeof Pole];

export const LegalCaseStatus = {
    OPEN: 'OPEN',
    CLOSED: 'CLOSED',
} as const satisfies Record<string, string>;

export type LegalCaseStatus = typeof LegalCaseStatus[keyof typeof LegalCaseStatus];

export interface SelectOption {
    value: string;
    label: string;
}

// Opções dos selects vêm do endpoint page-form; estas são o fallback local caso a
// chamada falhe, mantendo o formulário utilizável.
export const CASE_TYPE_OPTIONS: SelectOption[] = [
    { value: LegalCaseType.EXTRAJUDICIAL_WORKOUT, label: 'Recuperação extrajudicial' },
    { value: LegalCaseType.JUDICIAL_EXECUTION, label: 'Execução judicial' },
    { value: LegalCaseType.EXECUTION_DEFENSE, label: 'Embargos à execução' },
    { value: LegalCaseType.MONITORY, label: 'Ação monitória' },
    { value: LegalCaseType.JUDICIAL_REORGANIZATION, label: 'Recuperação judicial' },
    { value: LegalCaseType.LITIGATION_DEFENSE, label: 'Defesa em contencioso' },
];

export const POLE_OPTIONS: SelectOption[] = [
    { value: Pole.ACTIVE, label: 'Ativo' },
    { value: Pole.PASSIVE, label: 'Passivo' },
];

export const STATUS_OPTIONS: SelectOption[] = [
    { value: LegalCaseStatus.OPEN, label: 'Em curso' },
    { value: LegalCaseStatus.CLOSED, label: 'Encerrado' },
];

export interface LegalCaseListItem {
    id: string;
    caseType: LegalCaseType;
    caseTypeFormatted: string;
    status: LegalCaseStatus;
    statusFormatted: string;
    pole: Pole;
    poleFormatted: string;
    creditorEntity: string;
    externalCaseNumber: string | null;
    court: string | null;
    district: string | null;
    courtDivision: string | null;
    state: string | null;
    claimValue: number | null;
    claimValueFormatted: string | null;
    openedAt: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    // Resumo de acompanhamento (Task 6): o que é filtrável aparece na grid.
    nextDeadlineDate: string | null;
    nextDeadlineOverdue: boolean;
    pendingMeasureCount: number;
    pendingMeasureTypes: string[];
    pendingMeasureTypesFormatted: string[];
}

// Partes e papéis — Task 3.
export const PartyRole = {
    DEBTOR: 'DEBTOR',
    CREDITOR: 'CREDITOR',
    GUARANTOR: 'GUARANTOR',
    COLLATERAL_PROVIDER: 'COLLATERAL_PROVIDER',
    ASSIGNOR: 'ASSIGNOR',
    DRAWEE: 'DRAWEE',
    LAWYER: 'LAWYER',
    JUDICIAL_ADMINISTRATOR: 'JUDICIAL_ADMINISTRATOR',
    COUNTERPARTY: 'COUNTERPARTY',
} as const satisfies Record<string, string>;

export type PartyRole = typeof PartyRole[keyof typeof PartyRole];

// Fallback local dos papéis, caso o page-form não responda (espelha PARTY_ROLE_LABELS do backend).
export const PARTY_ROLE_OPTIONS: SelectOption[] = [
    { value: PartyRole.DEBTOR, label: 'Devedor' },
    { value: PartyRole.CREDITOR, label: 'Credor' },
    { value: PartyRole.GUARANTOR, label: 'Avalista' },
    { value: PartyRole.COLLATERAL_PROVIDER, label: 'Garantidor' },
    { value: PartyRole.ASSIGNOR, label: 'Cedente' },
    { value: PartyRole.DRAWEE, label: 'Sacado' },
    { value: PartyRole.LAWYER, label: 'Advogado' },
    { value: PartyRole.JUDICIAL_ADMINISTRATOR, label: 'Administrador judicial' },
    { value: PartyRole.COUNTERPARTY, label: 'Contraparte' },
];

export interface Party {
    id: string;
    legalCaseId: string;
    role: PartyRole;
    roleFormatted: string;
    personId: string | null;
    name: string | null;
    document: string | null;
    description: string | null;
    createdAt: string | null;
}

// Regra anti-duplicação (ADR 001): com personId, o backend zera name/document (a fonte é o
// person); sem personId, name é obrigatório.
export interface CreatePartyPayload {
    role: PartyRole;
    personId?: string;
    name?: string;
    document?: string;
    description?: string;
}

// Pretensão / Claim — Task 4.
export const BusinessReference = {
    INSTALLMENT: 'INSTALLMENT',
    RECEIVABLE: 'RECEIVABLE',
    GUARANTEE: 'GUARANTEE',
} as const satisfies Record<string, string>;

export type BusinessReference = typeof BusinessReference[keyof typeof BusinessReference];

// Fallback local (espelha BUSINESS_REFERENCE_LABELS do backend).
export const BUSINESS_REFERENCE_OPTIONS: SelectOption[] = [
    { value: BusinessReference.INSTALLMENT, label: 'Parcela' },
    { value: BusinessReference.RECEIVABLE, label: 'Recebível' },
    { value: BusinessReference.GUARANTEE, label: 'Garantia' },
];

export const ObligationType = {
    CPRF: 'CPRF',
    CD: 'CD',
    PJ: 'PJ',
    OPERATION: 'OPERATION',
    TITLE_DEBT: 'TITLE_DEBT',
} as const satisfies Record<string, string>;

export type ObligationType = typeof ObligationType[keyof typeof ObligationType];

// Fallback local (espelha OBLIGATION_TYPE_LABELS do backend).
export const OBLIGATION_TYPE_OPTIONS: SelectOption[] = [
    { value: ObligationType.CPRF, label: 'CPR-F' },
    { value: ObligationType.CD, label: 'Cédula de débito' },
    { value: ObligationType.PJ, label: 'Contrato PJ' },
    { value: ObligationType.OPERATION, label: 'Operação' },
    { value: ObligationType.TITLE_DEBT, label: 'Título (settlement)' },
];

export interface ClaimItem {
    id: string;
    titleNumber: string;
    businessReference: BusinessReference;
    businessReferenceFormatted: string;
    isGuarantee: boolean;
    amountAtFiling: number | null;
    amountAtFilingFormatted: string | null;
    dueDate: string | null;
    paidAmount: number;
    paidAmountFormatted: string | null;
    remainingAmount: number;
    remainingAmountFormatted: string | null;
    obligationType: ObligationType | null;
    obligationTypeFormatted: string;
    externalReference: string | null;
    obligationId: string | null;
    balanceSource: string;
    description: string | null;
    createdAt: string | null;
}

export interface Claim {
    id: string;
    legalCaseId: string;
    principal: number | null;
    principalFormatted: string | null;
    interest: number | null;
    interestFormatted: string | null;
    penalties: number | null;
    penaltiesFormatted: string | null;
    legalCosts: number | null;
    legalCostsFormatted: string | null;
    description: string | null;
    items: ClaimItem[];
    itemsTotal: number;
    itemsTotalFormatted: string | null;
    guaranteeTotal: number;
    guaranteeTotalFormatted: string | null;
    paidTotal: number;
    paidTotalFormatted: string | null;
    remainingTotal: number;
    remainingTotalFormatted: string | null;
    grandTotal: number;
    grandTotalFormatted: string | null;
    createdAt: string | null;
}

export interface CreateClaimPayload {
    principal?: number;
    interest?: number;
    penalties?: number;
    legalCosts?: number;
    description?: string;
}

// Regra anti-duplicação (ADR 002): garantia (GUARANTEE) fica fora do total; o backend
// bloqueia (422) somar INSTALLMENT + GUARANTEE da mesma operação (mesma externalReference).
export interface CreateClaimItemPayload {
    titleNumber: string;
    businessReference: BusinessReference;
    amountAtFiling: number;
    dueDate?: string;
    paidAmount?: number;
    obligationType?: ObligationType;
    externalReference?: string;
    obligationId?: string;
    description?: string;
}

// Andamento processual — Task 5.
export const CaseEventType = {
    FILING: 'FILING',
    PETITION: 'PETITION',
    DECISION: 'DECISION',
    HEARING: 'HEARING',
    CITATION: 'CITATION',
    SEIZURE: 'SEIZURE',
    APPEAL: 'APPEAL',
    SETTLEMENT_EVENT: 'SETTLEMENT_EVENT',
    OTHER: 'OTHER',
} as const satisfies Record<string, string>;

export type CaseEventType = typeof CaseEventType[keyof typeof CaseEventType];

// Fallback local (espelha CASE_EVENT_TYPE_LABELS do backend), caso o page-form não responda.
export const CASE_EVENT_TYPE_OPTIONS: SelectOption[] = [
    { value: CaseEventType.FILING, label: 'Distribuição' },
    { value: CaseEventType.PETITION, label: 'Petição' },
    { value: CaseEventType.DECISION, label: 'Decisão' },
    { value: CaseEventType.HEARING, label: 'Audiência' },
    { value: CaseEventType.CITATION, label: 'Citação/Intimação' },
    { value: CaseEventType.SEIZURE, label: 'Penhora' },
    { value: CaseEventType.APPEAL, label: 'Recurso' },
    { value: CaseEventType.SETTLEMENT_EVENT, label: 'Acordo' },
    { value: CaseEventType.OTHER, label: 'Andamento' },
];

export interface CaseEvent {
    id: string;
    legalCaseId: string;
    eventType: CaseEventType;
    eventTypeFormatted: string;
    eventDate: string | null;
    text: string;
    author: string | null;
    sheetReference: string | null;
    createdAt: string | null;
}

// Só o texto é obrigatório. A data do fato é opcional — o backend assume "hoje" quando
// ausente. O autor NÃO vai no payload: o backend o preenche a partir do contexto de
// autenticação (quem registrou).
export interface CreateCaseEventPayload {
    eventType: CaseEventType;
    eventDate?: string;
    text: string;
    sheetReference?: string;
}

// Medidas e prazos — Task 6.
export const LegalMeasureType = {
    SISBAJUD: 'SISBAJUD',
    INFOJUD: 'INFOJUD',
    RENAJUD: 'RENAJUD',
    CERTIFICATE_828_ISSUED: 'CERTIFICATE_828_ISSUED',
    CERTIFICATE_828_REGISTERED: 'CERTIFICATE_828_REGISTERED',
    PROTEST: 'PROTEST',
    ASSET_SEARCH: 'ASSET_SEARCH',
    BOOK_III: 'BOOK_III',
    PRIME: 'PRIME',
    CITATION: 'CITATION',
    DOOR_TO_DOOR_SEIZURE: 'DOOR_TO_DOOR_SEIZURE',
    SUSEP_CNSEG_LETTER: 'SUSEP_CNSEG_LETTER',
} as const satisfies Record<string, string>;

export type LegalMeasureType = typeof LegalMeasureType[keyof typeof LegalMeasureType];

// Fallback local (espelha LEGAL_MEASURE_TYPE_LABELS do backend), caso o page-form não responda.
export const LEGAL_MEASURE_TYPE_OPTIONS: SelectOption[] = [
    { value: LegalMeasureType.SISBAJUD, label: 'SISBAJUD' },
    { value: LegalMeasureType.INFOJUD, label: 'INFOJUD' },
    { value: LegalMeasureType.RENAJUD, label: 'RENAJUD' },
    { value: LegalMeasureType.CERTIFICATE_828_ISSUED, label: 'Certidão art. 828 expedida' },
    { value: LegalMeasureType.CERTIFICATE_828_REGISTERED, label: 'Certidão art. 828 registrada' },
    { value: LegalMeasureType.PROTEST, label: 'Protesto' },
    { value: LegalMeasureType.ASSET_SEARCH, label: 'Pesquisa de bens' },
    { value: LegalMeasureType.BOOK_III, label: 'Livro III' },
    { value: LegalMeasureType.PRIME, label: 'PRIME' },
    { value: LegalMeasureType.CITATION, label: 'Citação' },
    { value: LegalMeasureType.DOOR_TO_DOOR_SEIZURE, label: 'Penhora portas a dentro' },
    { value: LegalMeasureType.SUSEP_CNSEG_LETTER, label: 'Ofício SUSEP/CNSEG' },
];

export const LegalMeasureStatus = {
    REQUESTED: 'REQUESTED',
    PENDING: 'PENDING',
    GRANTED: 'GRANTED',
    DENIED: 'DENIED',
    FULFILLED: 'FULFILLED',
    EXPIRED: 'EXPIRED',
} as const satisfies Record<string, string>;

export type LegalMeasureStatus = typeof LegalMeasureStatus[keyof typeof LegalMeasureStatus];

// Fallback local (espelha LEGAL_MEASURE_STATUS_LABELS do backend).
export const LEGAL_MEASURE_STATUS_OPTIONS: SelectOption[] = [
    { value: LegalMeasureStatus.REQUESTED, label: 'Solicitada' },
    { value: LegalMeasureStatus.PENDING, label: 'Pendente' },
    { value: LegalMeasureStatus.GRANTED, label: 'Deferida' },
    { value: LegalMeasureStatus.DENIED, label: 'Indeferida' },
    { value: LegalMeasureStatus.FULFILLED, label: 'Cumprida' },
    { value: LegalMeasureStatus.EXPIRED, label: 'Prescrita' },
];

export const LegalDeadlineType = {
    DEFENSE: 'DEFENSE',
    APPEAL: 'APPEAL',
    COMPLIANCE: 'COMPLIANCE',
    MANIFESTATION: 'MANIFESTATION',
    DOCUMENT_SUBMISSION: 'DOCUMENT_SUBMISSION',
    HEARING: 'HEARING',
    PAYMENT: 'PAYMENT',
    OTHER: 'OTHER',
} as const satisfies Record<string, string>;

export type LegalDeadlineType = typeof LegalDeadlineType[keyof typeof LegalDeadlineType];

// Fallback local (espelha LEGAL_DEADLINE_TYPE_LABELS do backend).
export const LEGAL_DEADLINE_TYPE_OPTIONS: SelectOption[] = [
    { value: LegalDeadlineType.DEFENSE, label: 'Contestação/Defesa' },
    { value: LegalDeadlineType.APPEAL, label: 'Recurso' },
    { value: LegalDeadlineType.COMPLIANCE, label: 'Cumprimento' },
    { value: LegalDeadlineType.MANIFESTATION, label: 'Manifestação' },
    { value: LegalDeadlineType.DOCUMENT_SUBMISSION, label: 'Juntada de documento' },
    { value: LegalDeadlineType.HEARING, label: 'Audiência' },
    { value: LegalDeadlineType.PAYMENT, label: 'Pagamento' },
    { value: LegalDeadlineType.OTHER, label: 'Outro' },
];

export interface LegalMeasure {
    id: string;
    legalCaseId: string;
    measureType: LegalMeasureType;
    measureTypeFormatted: string;
    status: LegalMeasureStatus;
    statusFormatted: string;
    requestedAt: string | null;
    resolvedAt: string | null;
    description: string | null;
    createdAt: string | null;
}

// status default REQUESTED no backend quando ausente.
export interface CreateLegalMeasurePayload {
    measureType: LegalMeasureType;
    status?: LegalMeasureStatus;
    requestedAt?: string;
    resolvedAt?: string;
    description?: string;
}

export interface LegalDeadline {
    id: string;
    legalCaseId: string;
    deadlineType: LegalDeadlineType;
    deadlineTypeFormatted: string;
    dueDate: string | null;
    description: string | null;
    fulfilled: boolean;
    // Derivado na leitura pelo backend: vencido (dueDate < hoje) e não cumprido.
    overdue: boolean;
    createdAt: string | null;
}

// dueDate é obrigatória (o backend rejeita com 422 se ausente).
export interface CreateLegalDeadlinePayload {
    deadlineType: LegalDeadlineType;
    dueDate: string;
    description?: string;
    fulfilled?: boolean;
}

// Penhora com bem identificado — Task 7.
export const SeizedAssetType = {
    VEHICLE: 'VEHICLE',
    REAL_ESTATE: 'REAL_ESTATE',
    MACHINERY: 'MACHINERY',
    GRAINS: 'GRAINS',
    FINANCIAL: 'FINANCIAL',
    OTHER: 'OTHER',
} as const satisfies Record<string, string>;

export type SeizedAssetType = typeof SeizedAssetType[keyof typeof SeizedAssetType];

// Fallback local (espelha SEIZED_ASSET_TYPE_LABELS do backend), caso o page-form não responda.
export const SEIZED_ASSET_TYPE_OPTIONS: SelectOption[] = [
    { value: SeizedAssetType.VEHICLE, label: 'Veículo' },
    { value: SeizedAssetType.REAL_ESTATE, label: 'Imóvel' },
    { value: SeizedAssetType.MACHINERY, label: 'Maquinário' },
    { value: SeizedAssetType.GRAINS, label: 'Grãos' },
    { value: SeizedAssetType.FINANCIAL, label: 'Valores/direitos' },
    { value: SeizedAssetType.OTHER, label: 'Outro' },
];

export const SeizedAssetStatus = {
    LOCATED: 'LOCATED',
    GRANTED: 'GRANTED',
    EFFECTED: 'EFFECTED',
    CIRCULATION_RESTRICTED: 'CIRCULATION_RESTRICTED',
    RESTRICTION_LIFTED: 'RESTRICTION_LIFTED',
    APPREHENDED: 'APPREHENDED',
    APPRAISED: 'APPRAISED',
    AUCTIONED: 'AUCTIONED',
    UNSEIZABLE: 'UNSEIZABLE',
    LIEN_LIFTED: 'LIEN_LIFTED',
} as const satisfies Record<string, string>;

export type SeizedAssetStatus = typeof SeizedAssetStatus[keyof typeof SeizedAssetStatus];

// Fallback local (espelha SEIZED_ASSET_STATUS_LABELS do backend).
export const SEIZED_ASSET_STATUS_OPTIONS: SelectOption[] = [
    { value: SeizedAssetStatus.LOCATED, label: 'Localizado' },
    { value: SeizedAssetStatus.GRANTED, label: 'Penhora deferida' },
    { value: SeizedAssetStatus.EFFECTED, label: 'Penhora efetivada' },
    { value: SeizedAssetStatus.CIRCULATION_RESTRICTED, label: 'Restrição de circulação' },
    { value: SeizedAssetStatus.RESTRICTION_LIFTED, label: 'Baixa da restrição' },
    { value: SeizedAssetStatus.APPREHENDED, label: 'Apreendido' },
    { value: SeizedAssetStatus.APPRAISED, label: 'Avaliado' },
    { value: SeizedAssetStatus.AUCTIONED, label: 'Leiloado' },
    { value: SeizedAssetStatus.UNSEIZABLE, label: 'Impenhorável' },
    { value: SeizedAssetStatus.LIEN_LIFTED, label: 'Penhora levantada' },
];

// Estados terminais do ciclo da penhora (não avançam — o dropdown de status não os oferece
// como origem de nova transição, mas o backend é a autoridade que valida).
export const SEIZED_ASSET_TERMINAL_STATUSES: SeizedAssetStatus[] = [
    SeizedAssetStatus.UNSEIZABLE,
    SeizedAssetStatus.LIEN_LIFTED,
];

export interface SeizedAsset {
    id: string;
    legalCaseId: string;
    assetType: SeizedAssetType;
    assetTypeFormatted: string;
    status: SeizedAssetStatus;
    statusFormatted: string;
    description: string | null;
    collateralAssetUuid: string | null;
    grade: number | null;
    competingCreditor: string | null;
    estimatedValue: number | null;
    estimatedValueFormatted: string | null;
    appraisedValue: number | null;
    appraisedValueFormatted: string | null;
    // identificação por tipo (só a família do assetType vem preenchida)
    plate: string | null;
    renavam: string | null;
    chassis: string | null;
    state: string | null;
    registrationNumber: string | null;
    registryOffice: string | null;
    district: string | null;
    serialNumber: string | null;
    model: string | null;
    harvest: string | null;
    volume: string | null;
    warehouse: string | null;
    createdAt: string | null;
}

// status default LOCATED no backend quando ausente. Os campos de identificação são por
// tipo — só a família do assetType escolhido é enviada.
export interface CreateSeizedAssetPayload {
    assetType: SeizedAssetType;
    status?: SeizedAssetStatus;
    description?: string;
    collateralAssetUuid?: string;
    grade?: number;
    competingCreditor?: string;
    estimatedValue?: number;
    appraisedValue?: number;
    plate?: string;
    renavam?: string;
    chassis?: string;
    state?: string;
    registrationNumber?: string;
    registryOffice?: string;
    district?: string;
    serialNumber?: string;
    model?: string;
    harvest?: string;
    volume?: string;
    warehouse?: string;
}

// Edição parcial de um bem (valor, grau, credor concorrente, identificação). O status muda
// por rota própria (updateSeizedAssetStatus), não por aqui.
export type UpdateSeizedAssetPayload = Partial<Omit<CreateSeizedAssetPayload, 'assetType' | 'status'>>;

// Decisões e audiências — Task 8.
export const CourtDecisionType = {
    RULING: 'RULING',
    APPELLATE_DECISION: 'APPELLATE_DECISION',
    INJUNCTION: 'INJUNCTION',
    RATIFICATION: 'RATIFICATION',
    ORDER: 'ORDER',
} as const satisfies Record<string, string>;

export type CourtDecisionType = typeof CourtDecisionType[keyof typeof CourtDecisionType];

// Fallback local (espelha COURT_DECISION_TYPE_LABELS do backend), caso o page-form não responda.
export const COURT_DECISION_TYPE_OPTIONS: SelectOption[] = [
    { value: CourtDecisionType.RULING, label: 'Sentença' },
    { value: CourtDecisionType.APPELLATE_DECISION, label: 'Acórdão' },
    { value: CourtDecisionType.INJUNCTION, label: 'Liminar' },
    { value: CourtDecisionType.RATIFICATION, label: 'Homologação' },
    { value: CourtDecisionType.ORDER, label: 'Despacho' },
];

export const HearingOutcome = {
    SCHEDULED: 'SCHEDULED',
    HELD: 'HELD',
    SETTLED: 'SETTLED',
    NOT_SETTLED: 'NOT_SETTLED',
    ADJOURNED: 'ADJOURNED',
    CANCELED: 'CANCELED',
} as const satisfies Record<string, string>;

export type HearingOutcome = typeof HearingOutcome[keyof typeof HearingOutcome];

// Fallback local (espelha HEARING_OUTCOME_LABELS do backend).
export const HEARING_OUTCOME_OPTIONS: SelectOption[] = [
    { value: HearingOutcome.SCHEDULED, label: 'Agendada' },
    { value: HearingOutcome.HELD, label: 'Realizada' },
    { value: HearingOutcome.SETTLED, label: 'Acordo firmado' },
    { value: HearingOutcome.NOT_SETTLED, label: 'Sem acordo' },
    { value: HearingOutcome.ADJOURNED, label: 'Adiada' },
    { value: HearingOutcome.CANCELED, label: 'Cancelada' },
];

export interface CourtDecision {
    id: string;
    legalCaseId: string;
    decisionType: CourtDecisionType;
    decisionTypeFormatted: string;
    decisionDate: string | null;
    summary: string | null;
    createdAt: string | null;
}

// decisionDate é obrigatória (o backend rejeita com 422 se ausente). A decisão não tem ciclo
// de status — é fato congelado; a edição é de campos.
export interface CreateCourtDecisionPayload {
    decisionType: CourtDecisionType;
    decisionDate: string;
    summary?: string;
}

export type UpdateCourtDecisionPayload = Partial<CreateCourtDecisionPayload>;

export interface Hearing {
    id: string;
    legalCaseId: string;
    scheduledAt: string | null;
    heldAt: string | null;
    // Derivado na leitura pelo backend: realizada quando heldAt está preenchida.
    held: boolean;
    outcome: string | null;
    outcomeStatus: HearingOutcome | null;
    outcomeStatusFormatted: string;
    description: string | null;
    createdAt: string | null;
}

// scheduledAt é obrigatória (o backend rejeita com 422 se ausente). Marcar realizada =
// enviar heldAt + outcome. "Realizada" deriva da presença de heldAt, não há enum de status.
export interface CreateHearingPayload {
    scheduledAt: string;
    heldAt?: string;
    outcome?: string;
    outcomeStatus?: HearingOutcome;
    description?: string;
}

export type UpdateHearingPayload = Partial<CreateHearingPayload>;

export interface LegalCaseDetail extends LegalCaseListItem {
    courtLink: string | null;
    description: string | null;
    parties: Party[];
    claim: Claim | null;
    events: CaseEvent[];
    measures: LegalMeasure[];
    deadlines: LegalDeadline[];
    seizedAssets: SeizedAsset[];
    courtDecisions: CourtDecision[];
    hearings: Hearing[];
}

export interface LegalCaseFormOptions {
    caseTypes: SelectOption[];
    poles: SelectOption[];
    statuses: SelectOption[];
    partyRoles: SelectOption[];
    businessReferences: SelectOption[];
    obligationTypes: SelectOption[];
    caseEventTypes: SelectOption[];
    legalMeasureTypes: SelectOption[];
    legalMeasureStatuses: SelectOption[];
    legalDeadlineTypes: SelectOption[];
    seizedAssetTypes: SelectOption[];
    seizedAssetStatuses: SelectOption[];
    courtDecisionTypes: SelectOption[];
    hearingOutcomes: SelectOption[];
}

export interface CreateLegalCasePayload {
    caseType: string;
    pole: string;
    creditorEntity: string;
    status?: string;
    openedAt?: string;
    externalCaseNumber?: string;
    court?: string;
    district?: string;
    courtDivision?: string;
    state?: string;
    courtLink?: string;
    claimValue?: number;
    description?: string;
}
